/**
 * WebSocket wire protocol for the convergence commit path.
 *
 * TypeScript mirror of src/lambda/shared/GoobDoobVtt.Domain/ConvergenceMessages.cs.
 * Keep them in lockstep — when one changes, the other has to change too.
 */

import type {
  ClassResource,
  ConvergenceDraft,
  ConvergenceFeature,
  ConvergenceProfile,
  Deed,
  FeatureCost,
} from './convergence';

export const ConvergenceClientActions = {
  /** Client→server: commit a ratified merge. */
  Commit: 'convergence/commit',
  /** Client→server: persist the in-progress galaxy draft (owner while unlocked, or DM). */
  DraftSave: 'convergence/draft/save',
  /** Client→server: DM-only — lock/unlock a character's draft. */
  DraftLock: 'convergence/draft/lock',
  /** Client→server: DM-only — unbind (revert) a committed merge. */
  Unbind: 'convergence/unbind',
  /** Client→server: DM-only — append a session's deed digest(s) to a character's ledger. */
  DeedAppend: 'convergence/deed/append',
  /** Client→server: DM-only — commit a ratified level-up (new FEATURE rows + profile level bump). */
  Level: 'convergence/level',
  /** Client→server: DM-only — annotate a committed merge (resources / feature costs / variant label). */
  Annotate: 'convergence/annotate',
} as const;

export const ConvergenceServerTypes = {
  /** Server→client: a merge was committed (broadcast to the session). */
  Committed: 'convergence/committed',
  /** Server→client: a draft was saved/updated (broadcast to owner + DM). */
  DraftSaved: 'convergence/draft/saved',
  /** Server→client: a draft's lock state changed (broadcast to owner + DM). */
  DraftLocked: 'convergence/draft/locked',
  /** Server→originator: a draft save/lock was rejected. */
  DraftRejected: 'convergence/draft/rejected',
  /** Server→client: a committed merge was unbound (broadcast to the session). */
  Unbound: 'convergence/unbound',
  /** Server→client: deeds were appended to a ledger (owner + DM only). */
  DeedAppended: 'convergence/deed/appended',
  /** Server→client: a level-up was committed (broadcast to the session). */
  Leveled: 'convergence/leveled',
  /** Server→client: a committed merge was annotated (broadcast to the session; the sheet refetches). */
  Annotated: 'convergence/annotated',
} as const;

// ── Annotate: describe a committed merge's resources + costs without re-running it ──

/**
 * DM-only. Every field is a full REPLACE of that facet when present and left
 * alone when absent, so the DM can fix one resource's wording without
 * resending the feature costs. `expectedVersion` is the profile version the
 * client last saw (0 = skip the check).
 */
export interface ConvergenceAnnotateRequest {
  action: typeof ConvergenceClientActions.Annotate;
  sessionId: string;
  characterId: string;
  expectedVersion: number;
  /** The class's resource definitions (replaces the whole list). */
  resources?: ClassResource[];
  /** Per-feature costs, keyed by feature seq (replaces that feature's costs). */
  featureCosts?: Array<{ seq: number; costs: FeatureCost[] }>;
  /** The DM's name for this merge variant (MergeRunRef.variantLabel). */
  variantLabel?: string;
  reason?: string;
}

export interface ConvergenceAnnotatedPayload {
  characterId: string;
  name: string;
  version: number;
  resources?: ClassResource[];
  variantLabel?: string;
  /** Feature seqs whose costs changed. */
  featureSeqs: number[];
}

// ── Layer 4: deed ledger + level-up ──

/** DM-only: append one or more session digests to CHAR#{id}/DEED#{seq}. seq is assigned server-side. */
export interface ConvergenceDeedAppendRequest {
  action: typeof ConvergenceClientActions.DeedAppend;
  sessionId: string;
  characterId: string;
  deeds: Array<Omit<Deed, 'seq'> & { seq?: number }>;
  reason?: string;
}

export interface ConvergenceDeedAppendedPayload {
  characterId: string;
  /** The rows as stored (with their assigned seqs). */
  deeds: Deed[];
  count: number;
}

/** DM-only: commit a ratified level-up. `expectedVersion` is the profile version the client last saw. */
export interface ConvergenceLevelRequest {
  action: typeof ConvergenceClientActions.Level;
  sessionId: string;
  characterId: string;
  toLevel: number;
  expectedVersion: number;
  /** The ratified feature(s); seq is (re)assigned server-side to continue the existing sequence. */
  features: ConvergenceFeature[];
  /** DEED# refs that drove this level (stamped on every feature row). */
  sourceDeedRefs?: string[];
  /** HP gained from this level's hit die (rolled or average); omitted = server takes the average of leanings.hitDie. */
  hitDieRoll?: number;
  reason?: string;
}

export interface ConvergenceLeveledPayload {
  characterId: string;
  name: string;
  level: number;
  version: number;
  cpSpent: number;
  /** Running HP gained from hit dice since the merge (after this level). */
  hitDieGained?: number;
  featureCount: number;
  /** The newly written feature rows. */
  features: ConvergenceFeature[];
}

export interface ConvergenceCommitRequest {
  action: typeof ConvergenceClientActions.Commit;
  /** Scopes the audit + broadcast. The CHAR#{id} rows are not session-scoped. */
  sessionId: string;
  profile: ConvergenceProfile;
  features: ConvergenceFeature[];
  reason?: string;
}

export interface ConvergenceCommittedPayload {
  characterId: string;
  name: string;
  level: number;
  version: number;
  featureCount: number;
}

/** DM-only inverse of commit: revert a committed merge and restore the draft. */
export interface ConvergenceUnbindRequest {
  action: typeof ConvergenceClientActions.Unbind;
  sessionId: string;
  characterId: string;
  reason?: string;
}

export interface ConvergenceUnboundPayload {
  characterId: string;
  name: string;
  draftRestored: boolean;
  /** The restored galaxy draft (absent when an existing draft was left untouched). */
  draft?: ConvergenceDraft;
}

export interface ConvergenceDraftSaveRequest {
  action: typeof ConvergenceClientActions.DraftSave;
  sessionId: string;
  draft: ConvergenceDraft;
  /** Optimistic concurrency: the version the client last knew (0 = first save). */
  expectedVersion: number;
  reason?: string;
}

export interface ConvergenceDraftLockRequest {
  action: typeof ConvergenceClientActions.DraftLock;
  sessionId: string;
  characterId: string;
  locked: boolean;
  reason?: string;
}

/** Broadcast on a draft save OR lock change (to owner + DM). Carries the full current draft. */
export interface ConvergenceDraftSavedPayload {
  characterId: string;
  version: number;
  locked: boolean;
  updatedBy?: string;
  updatedAt?: string;
  draft?: ConvergenceDraft;
}

export type ConvergenceDraftRejectReason =
  | 'forbidden'
  | 'locked'
  | 'version_conflict'
  | 'no_draft';

export interface ConvergenceDraftRejectedPayload {
  characterId: string;
  reason: ConvergenceDraftRejectReason;
  version: number;
  /** Server-current draft for the client to reconcile (on version_conflict / locked). */
  draft?: ConvergenceDraft;
}
